// src/pages/customer-panel/requests/RequestsPage.js
// Request Management Page - Full Lifecycle

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Snackbar,
    InputAdornment,
    Tooltip,
    Tabs,
    Tab,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContactsIcon from '@mui/icons-material/Contacts';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import TimelineIcon from '@mui/icons-material/Timeline';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import BlockIcon from '@mui/icons-material/Block';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const STATUS_CONFIG = {
    new: { color: '#0ea5e9', bg: '#e0f2fe', label: 'New' },
    invited: { color: '#6366f1', bg: '#e0e7ff', label: 'Invited' },
    active: { color: '#10b981', bg: '#d1fae5', label: 'Active' },
    contacted: { color: '#f59e0b', bg: '#fef3c7', label: 'Contacted' },
    qualified: { color: '#10b981', bg: '#d1fae5', label: 'Qualified' },
    declined: { color: '#ef4444', bg: '#fee2e2', label: 'Declined' },
    lost: { color: '#ef4444', bg: '#fee2e2', label: 'Lost' },
};

const STATUS_TABS = ['all', 'new', 'invited', 'active', 'declined'];

const INITIAL_FORM = {
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    source: '',
    notes: '',
};

// --- Helper: Debounce Hook ---
function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

// --- Helper: Format Date ---
function formatDate(dateStr) {
    if (!dateStr) return '--';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return '--';
    }
}

function formatDateTime(dateStr) {
    if (!dateStr) return '--';
    try {
        return new Date(dateStr).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '--';
    }
}

// --- Status Chip ---
function StatusChip({ status }) {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.new;
    return (
        <Chip
            label={config.label}
            size="small"
            sx={{
                fontFamily,
                fontWeight: 600,
                fontSize: '0.75rem',
                bgcolor: config.bg,
                color: config.color,
            }}
        />
    );
}

// --- Invite Request Modal ---
function InviteRequestModal({ request, open, onClose, onInvite }) {
    const [formData, setFormData] = useState({ fname: '', lname: '', email: '', role: 'customer' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (request && open) {
            setFormData({
                fname: request.first_name || request.name?.split(' ')[0] || '',
                lname: request.last_name || request.name?.split(' ').slice(1).join(' ') || '',
                email: request.email || '',
                role: 'customer',
            });
        }
    }, [request, open]);

    if (!request) return null;

    const handleSubmit = async () => {
        if (!formData.fname.trim() || !formData.email.trim()) return;
        setSubmitting(true);
        try {
            await onInvite(request.id || request._id, formData);
            onClose();
        } catch (err) {
            console.error('Error inviting customer:', err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                Invite Customer: {request.first_name || request.name} {request.last_name || ''}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="First Name"
                            fullWidth
                            value={formData.fname}
                            onChange={(e) => setFormData((p) => ({ ...p, fname: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <TextField
                            label="Last Name"
                            fullWidth
                            value={formData.lname}
                            onChange={(e) => setFormData((p) => ({ ...p, lname: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                    </Box>
                    <TextField
                        label="Email Address"
                        type="email"
                        fullWidth
                        value={formData.email}
                        onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                        sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting || !formData.fname.trim() || !formData.email.trim()}
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <PersonAddIcon />}
                    sx={{
                        fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px',
                        textTransform: 'none', '&:hover': { bgcolor: '#059669' },
                    }}
                >
                    Send Invitation
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Update Status Modal ---
function UpdateStatusModal({ request, targetStatus, open, onClose, onUpdate }) {
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) setDescription('');
    }, [open]);

    if (!request) return null;

    const titles = { decline: 'Decline Request', new: 'Refresh Request' };
    const cleanText = (text) => (text || '').replace(/\n\n!---------------!/g, '');

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            await onUpdate(request.id || request._id, { status: targetStatus === 'decline' ? 'declined' : targetStatus, description });
            onClose();
        } catch (err) {
            console.error('Error updating status:', err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                {titles[targetStatus] || 'Update Request Status'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569' }}>
                        You are about to change the status of this request to{' '}
                        <strong style={{ textTransform: 'capitalize' }}>{targetStatus}</strong>.
                        You can add an optional note below.
                    </Typography>
                    <TextField
                        multiline
                        rows={4}
                        fullWidth
                        placeholder="Reason or completion notes..."
                        value={cleanText(description)}
                        onChange={(e) => setDescription(e.target.value)}
                        sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={submitting}
                    sx={{
                        fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px',
                        textTransform: 'none', '&:hover': { bgcolor: '#059669' },
                    }}
                >
                    {submitting ? 'Updating...' : 'Confirm Update'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Request Detail Modal (Journey Timeline) ---
function RequestDetailModal({ request, open, onClose }) {
    if (!request) return null;

    const timelineEvents = [
        {
            status: 'new', title: 'Request Created',
            description: `A new service request was submitted by ${request.first_name || request.name || ''} ${request.last_name || ''} for "${request.professional_name || 'service'}". Waiting for a response from the team.`,
            timestamp: request.created_on || request.created_at || request.createdAt,
            icon: <FiberNewIcon />, color: '#0ea5e9', bg: '#e0f2fe', show: true,
        },
        {
            status: 'invited', title: 'Customer Invitation Sent',
            description: `An invitation was sent to ${request.email} to join the customer portal. The customer can now set up their account and track progress.`,
            timestamp: null, icon: <MailOutlineIcon />, color: '#6366f1', bg: '#e0e7ff',
            show: request.status !== 'new' && request.status !== 'declined' && request.customer_details,
        },
        {
            status: 'active', title: 'Customer Account Activated',
            description: `${request.first_name || request.name || ''} ${request.last_name || ''} has activated their account and completed profile setup. Ready to be assigned.`,
            timestamp: null, icon: <HowToRegIcon />, color: '#10b981', bg: '#d1fae5',
            show: ['active'].includes(request.status),
        },
        {
            status: 'declined', title: 'Request Declined',
            description: 'This request was declined. The customer has been notified and no further action will be taken.',
            timestamp: null, icon: <BlockIcon />, color: '#ef4444', bg: '#fee2e2',
            show: request.status === 'declined',
        },
    ];

    const visibleEvents = timelineEvents.filter((e) => e.show);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimelineIcon sx={{ color: GREEN_ACCENT }} />
                    Request Journey: #{request.id || request._id} - {request.first_name || request.name} {request.last_name || ''}
                </Box>
                <StatusChip status={request.status} />
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                    {/* Left Column: Timeline */}
                    <Box sx={{ flex: 2, maxHeight: 600, overflow: 'auto', pr: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', mb: 2, color: '#1e293b' }}>
                            Progress Timeline
                        </Typography>
                        <Box sx={{ position: 'relative', pl: 4 }}>
                            {/* Vertical line */}
                            <Box sx={{
                                position: 'absolute', left: 15, top: 0, bottom: 0, width: 2,
                                bgcolor: '#e2e8f0',
                            }} />
                            {visibleEvents.map((event, index) => (
                                <Box key={event.status + index} sx={{ position: 'relative', mb: 3 }}>
                                    {/* Icon circle */}
                                    <Box sx={{
                                        position: 'absolute', left: -25, top: 0,
                                        width: 32, height: 32, borderRadius: '50%',
                                        bgcolor: event.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: '3px solid white', boxShadow: '0 0 0 1px ' + event.color,
                                        '& svg': { fontSize: 16, color: event.color },
                                    }}>
                                        {event.icon}
                                    </Box>
                                    {/* Content card */}
                                    <Paper sx={{
                                        p: 2.5, borderRadius: '12px', border: '1px solid #f1f5f9',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                    }}>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', mb: 0.5 }}>
                                            {event.title}
                                        </Typography>
                                        {event.timestamp && (
                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mb: 1 }}>
                                                {formatDateTime(event.timestamp)}
                                            </Typography>
                                        )}
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', lineHeight: 1.6 }}>
                                            {event.description}
                                        </Typography>
                                    </Paper>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Right Column: Details */}
                    <Box sx={{ flex: 1, maxHeight: 600, overflow: 'auto' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', mb: 2, color: '#1e293b' }}>
                            Request Details
                        </Typography>

                        {/* Customer Information */}
                        <Paper sx={{ p: 2.5, borderRadius: '12px', bgcolor: '#f8fafc', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <PersonIcon sx={{ fontSize: 18, color: GREEN_ACCENT }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>
                                    Customer Information
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                <DetailRow label="Name" value={`${request.first_name || request.name || ''} ${request.last_name || ''}`} />
                                <DetailRow label="Email" value={request.email || '--'} />
                                <DetailRow label="Phone" value={request.phone || 'Not provided'} />
                                {(request.street_address || request.city) && (
                                    <>
                                        <Divider sx={{ my: 0.5 }} />
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>Address:</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                            {request.street_address && <>{request.street_address}<br /></>}
                                            {[request.city, request.state, request.zip_code].filter(Boolean).join(', ')}
                                            {request.country && <><br />{request.country}</>}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Paper>

                        {/* Service Request */}
                        {(request.professional_name || request.situation_description || request.source || request.notes) && (
                            <Paper sx={{ p: 2.5, borderRadius: '12px', bgcolor: '#f8fafc', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <DescriptionIcon sx={{ fontSize: 18, color: GREEN_ACCENT }} />
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>
                                        Service Request
                                    </Typography>
                                </Box>
                                {request.professional_name && (
                                    <Box sx={{ mb: 1 }}>
                                        <Chip label={request.professional_name} size="small" sx={{
                                            fontFamily, fontWeight: 600, fontSize: '0.75rem',
                                            bgcolor: '#d1fae5', color: GREEN_ACCENT,
                                        }} />
                                    </Box>
                                )}
                                {(request.situation_description || request.notes) && (
                                    <Box sx={{ bgcolor: 'white', borderRadius: '8px', p: 1.5, maxHeight: 120, overflow: 'auto' }}>
                                        <Typography
                                            component="pre"
                                            sx={{ fontFamily, fontSize: '0.75rem', color: '#475569', whiteSpace: 'pre-wrap', m: 0 }}
                                        >
                                            {request.situation_description || request.notes}
                                        </Typography>
                                    </Box>
                                )}
                                {request.expectation && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: '#475569', mb: 0.5 }}>
                                            Customer Expectations:
                                        </Typography>
                                        <Box sx={{ bgcolor: 'white', borderRadius: '8px', p: 1.5, maxHeight: 100, overflow: 'auto' }}>
                                            <Typography
                                                component="pre"
                                                sx={{ fontFamily, fontSize: '0.75rem', color: '#475569', whiteSpace: 'pre-wrap', m: 0 }}
                                            >
                                                {request.expectation}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                {request.source && <DetailRow label="Source" value={request.source} />}
                            </Paper>
                        )}

                        {/* Timestamps */}
                        <Paper sx={{ p: 2.5, borderRadius: '12px', bgcolor: '#f8fafc' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <AccessTimeIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>
                                    Timeline
                                </Typography>
                            </Box>
                            <DetailRow label="Created" value={formatDateTime(request.created_on || request.created_at || request.createdAt)} />
                            <DetailRow label="Last Updated" value={formatDateTime(request.updated_on || request.updated_at || request.updatedAt)} />
                        </Paper>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} sx={{
                    fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none',
                    bgcolor: '#f1f5f9', borderRadius: '10px', px: 3, '&:hover': { bgcolor: '#e2e8f0' },
                }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Detail Row Helper ---
function DetailRow({ label, value }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
                {label}:
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', textAlign: 'right', wordBreak: 'break-all', maxWidth: '60%' }}>
                {value || '--'}
            </Typography>
        </Box>
    );
}

// --- Main Page ---
export default function RequestsPage() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Modal states
    const [detailModalRequest, setDetailModalRequest] = useState(null);
    const [inviteModalRequest, setInviteModalRequest] = useState(null);
    const [updateStatusModal, setUpdateStatusModal] = useState(null);

    // User role
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team?.toLowerCase() || 'customer';
    const isAdmin = userRole === 'admin' || userRole === 'owner';

    const showSnack = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                search,
                page: page + 1,
                limit: rowsPerPage,
            };
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            const response = await axiosInstance.get('/website-signup-requests', { params });
            const data = response.data;
            setRequests(data.requests || data.data || []);
            setTotalCount(data.total || data.totalCount || data.pagination?.total || 0);
        } catch (err) {
            console.error('Failed to fetch requests:', err);
            setError(err.response?.data?.message || 'Failed to load requests.');
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, page, rowsPerPage]);

    useEffect(() => {
        document.title = 'Requests | Customer Panel';
        fetchRequests();
    }, [fetchRequests]);

    const handleOpenDialog = () => {
        setFormData(INITIAL_FORM);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setFormData(INITIAL_FORM);
    };

    const handleSubmit = async () => {
        const name = formData.name || `${formData.first_name} ${formData.last_name}`.trim();
        if (!name.trim() || !formData.email.trim()) {
            showSnack('Name and email are required.', 'warning');
            return;
        }
        setSubmitting(true);
        try {
            await axiosInstance.post('/website-signup-requests', formData);
            showSnack('Request added successfully!');
            handleCloseDialog();
            fetchRequests();
        } catch (err) {
            console.error('Failed to add request:', err);
            showSnack(err.response?.data?.message || 'Failed to add request.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (request) => {
        if (!window.confirm(`Delete request "${request.name || request.first_name || ''}"?`)) return;
        try {
            await axiosInstance.delete(`/website-signup-requests/${request.id || request._id}`);
            showSnack('Request deleted successfully!');
            fetchRequests();
        } catch (err) {
            console.error('Failed to delete request:', err);
            showSnack(err.response?.data?.message || 'Failed to delete request.', 'error');
        }
    };

    const handleUpdateRequestStatus = async (requestId, payload) => {
        try {
            await axiosInstance.put(`/website-signup-requests/${requestId}/status`, payload);
            showSnack('Request status updated successfully!');
            fetchRequests();
        } catch (err) {
            console.error('Failed to update request status:', err);
            showSnack(err.response?.data?.message || 'Failed to update request status.', 'error');
            throw err;
        }
    };

    const handleInviteCustomer = async (requestId, inviteData) => {
        try {
            await axiosInstance.post(`/website-signup-requests/${requestId}/invite`, inviteData);
            showSnack('Customer invitation sent successfully!');
            fetchRequests();
        } catch (err) {
            console.error('Failed to invite customer:', err);
            showSnack(err.response?.data?.message || 'Failed to invite customer.', 'error');
            throw err;
        }
    };

    const handleDeclineRequest = async (request) => {
        if (!window.confirm('Are you sure you want to decline this request? This action cannot be undone.')) return;
        try {
            await handleUpdateRequestStatus(request.id || request._id, { status: 'declined' });
        } catch (err) { /* handled in handleUpdateRequestStatus */ }
    };

    // Action buttons per row based on status and role
    const getActionButtons = (request) => {
        const buttons = [];
        const requestId = request.id || request._id;

        // Always show View
        buttons.push(
            <Tooltip title="View Journey" key="view">
                <IconButton size="small" onClick={() => setDetailModalRequest(request)} sx={{ color: '#3b82f6' }}>
                    <VisibilityIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        );

        // Admin: Accept (invite) + Decline for new requests
        if (isAdmin && request.status === 'new') {
            buttons.push(
                <Tooltip title="Accept & Invite" key="invite">
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => setInviteModalRequest(request)}
                        sx={{
                            fontFamily, fontWeight: 600, fontSize: '0.7rem',
                            bgcolor: GREEN_ACCENT, textTransform: 'none', minWidth: 0, px: 1.5, py: 0.5,
                            borderRadius: '8px', '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        Accept
                    </Button>
                </Tooltip>,
                <Tooltip title="Decline" key="decline">
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleDeclineRequest(request)}
                        sx={{
                            fontFamily, fontWeight: 600, fontSize: '0.7rem',
                            bgcolor: '#ef4444', textTransform: 'none', minWidth: 0, px: 1.5, py: 0.5,
                            borderRadius: '8px', '&:hover': { bgcolor: '#dc2626' },
                        }}
                    >
                        Decline
                    </Button>
                </Tooltip>
            );
        }

        // Admin: Delete for non-terminal statuses
        if (isAdmin && request.status !== 'declined') {
            buttons.push(
                <Tooltip title="Delete" key="delete">
                    <IconButton size="small" onClick={() => handleDelete(request)} sx={{ color: '#ef4444' }}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            );
        }

        return buttons;
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        Request Management
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                        Manage and track your requests through the full lifecycle
                    </Typography>
                </Box>
                {isAdmin && (
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={handleOpenDialog}
                        sx={{
                            fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px',
                            textTransform: 'none', px: 3, '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        Add Request
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>
                    {error}
                </Alert>
            )}

            <Paper
                sx={{
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                }}
            >
                {/* Status Tabs */}
                <Tabs
                    value={statusFilter}
                    onChange={(e, newVal) => { setStatusFilter(newVal); setPage(0); }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        px: 2,
                        borderBottom: '1px solid',
                        borderColor: 'grey.100',
                        '& .MuiTab-root': {
                            fontFamily, fontWeight: 600, textTransform: 'none', minHeight: 48, minWidth: 'auto',
                        },
                        '& .Mui-selected': { color: GREEN_ACCENT },
                        '& .MuiTabs-indicator': { bgcolor: GREEN_ACCENT },
                    }}
                >
                    {STATUS_TABS.map((tab) => (
                        <Tab
                            key={tab}
                            label={tab.charAt(0).toUpperCase() + tab.slice(1)}
                            value={tab}
                        />
                    ))}
                </Tabs>

                {/* Search */}
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <TextField
                        placeholder="Search requests..."
                        size="small"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        sx={{
                            flex: 1, maxWidth: 400,
                            '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchRequests} sx={{ color: '#64748b' }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                {['ID', 'Customer', 'Email', 'Status', 'Created', 'Actions'].map((header) => (
                                    <TableCell
                                        key={header}
                                        sx={{
                                            fontFamily, fontWeight: 700, color: '#64748b',
                                            fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px',
                                        }}
                                    >
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                        <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
                                    </TableCell>
                                </TableRow>
                            ) : requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                        <ContactsIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No requests found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map((request) => (
                                    <TableRow
                                        key={request.id || request._id}
                                        sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                                    >
                                        <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                            #{request.id || request._id}
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>
                                                {request.first_name || request.name || '--'} {request.last_name || ''}
                                            </Typography>
                                            {request.phone && (
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                                    {request.phone}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                            {request.email || '--'}
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={request.status} />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                            {formatDate(request.created_on || request.created_at || request.createdAt)}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                {getActionButtons(request)}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    sx={{ '& *': { fontFamily } }}
                />
            </Paper>

            {/* Add Request Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Add New Request
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="First Name"
                                fullWidth
                                value={formData.first_name}
                                onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                            />
                            <TextField
                                label="Last Name"
                                fullWidth
                                value={formData.last_name}
                                onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                            />
                        </Box>
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            value={formData.email}
                            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <TextField
                            label="Phone"
                            fullWidth
                            value={formData.phone}
                            onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <TextField
                            label="Source"
                            fullWidth
                            placeholder="e.g., Website, Referral, Social Media"
                            value={formData.source}
                            onChange={(e) => setFormData((prev) => ({ ...prev, source: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <TextField
                            label="Notes"
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.notes}
                            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{
                            fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px',
                            textTransform: 'none', '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        Add Request
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Lifecycle Modals */}
            <RequestDetailModal
                request={detailModalRequest}
                open={!!detailModalRequest}
                onClose={() => setDetailModalRequest(null)}
            />
            <InviteRequestModal
                request={inviteModalRequest}
                open={!!inviteModalRequest}
                onClose={() => setInviteModalRequest(null)}
                onInvite={handleInviteCustomer}
            />
            <UpdateStatusModal
                request={updateStatusModal?.request ?? null}
                targetStatus={updateStatusModal?.status ?? 'decline'}
                open={!!updateStatusModal}
                onClose={() => setUpdateStatusModal(null)}
                onUpdate={handleUpdateRequestStatus}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
